import { Children, cloneElement, isValidElement, useState } from 'react';

import { FunnelProps, StepProps, StepType } from './useFunnel.types';

export function useFunnel<T extends StepType>(steps: T[], initialStep?: T) {
  const [currentStepIndex, setCurrentStepIndex] = useState(
    initialStep ? steps.indexOf(initialStep) : 0
  );

  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const nextStep = () =>
    setCurrentStepIndex((prevIndex) => {
      const hasNextStep = prevIndex < steps.length - 1;

      return hasNextStep ? prevIndex + 1 : prevIndex;
    });

  const prevStep = () =>
    setCurrentStepIndex((prevIndex) => {
      const hasPreviousStep = prevIndex > 0;

      return hasPreviousStep ? prevIndex - 1 : prevIndex;
    });

  const goToStep = (step: T) => {
    const stepIndex = steps.indexOf(step);
    if (stepIndex !== -1) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const Funnel = <T extends string>({
    steps,
    initialStep,
    onComplete,
    children,
  }: FunnelProps<T>) => {
    const { currentStep, nextStep, prevStep, isFirstStep, isLastStep } =
      useFunnel(steps, initialStep);

    const handleNext = () => {
      if (isLastStep && onComplete) {
        onComplete();
      } else {
        nextStep();
      }
    };

    const childrenArray = Children.toArray(children);

    const currentStepElement = childrenArray.find(
      (child) => isValidElement(child) && child.props.name === currentStep
    );

    if (!isValidElement(currentStepElement)) {
      return null;
    }

    return (
      <>
        {cloneElement(currentStepElement.props.children, {
          isFirstStep,
          isLastStep,
          onNext: handleNext,
          onPrev: prevStep,
        })}
      </>
    );
  };

  const Step = <T extends string>({ name, children }: StepProps<T>) => {
    return children;
  };

  return {
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    isFirstStep,
    isLastStep,
    Funnel,
    Step,
  };
}
